import React from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader,
  AlertDialogOverlay, Box, Button, Flex, HStack, Icon, Stack, Text, VStack,
} from "@chakra-ui/react";
import { FiAlertTriangle, FiTrash2 } from "react-icons/fi";
import { IconType } from "react-icons";
import { alpha, useOppTheme } from "./useOppTheme";
import { blurClick } from "./blurClick";

/**
 * The module's ONE destructive confirmation (POLISH v1: "sensitive = two-step", and the
 * cascade-aware-mutations rule in how-to-build-new-module — a confirm that NAMES what else goes).
 *
 * It replaces `window.confirm` everywhere in this module. `window.confirm` was wrong on four counts:
 * it is unstyled OS chrome in the middle of a branded page, it cannot say what else will be deleted,
 * its buttons are "OK"/"Cancel" rather than words about the act, and it blocks the whole page so a
 * browser test or a screenshot sweep wedges on it.
 *
 * `cascade` is the honest list of what else disappears — one line per thing, with real counts read
 * from the record, never a vague "and related data".
 */
export interface OppConfirmProps {
  isOpen: boolean;
  onClose: () => void;
  /** Runs only after the person presses the confirm button. Awaited: the dialog shows a spinner until it settles. */
  onConfirm: () => void | Promise<unknown>;
  /** "Delete this opportunity?" — a question, naming the thing. */
  title: string;
  /** What happens, in plain words. */
  body: React.ReactNode;
  /** Everything else that goes with it. Shown as a list, so nothing is a surprise. */
  cascade?: string[];
  /** The words on the button that does it — an act, never "OK". */
  confirmLabel: string;
  /** Words on the way out. Defaults to "Keep it". */
  cancelLabel?: string;
  icon?: IconType;
  /** `danger` (default) for deletes; `warning` for a change that notifies people. */
  tone?: "danger" | "warning";
  isLoading?: boolean;
}

export function OppConfirm({
  isOpen, onClose, onConfirm, title, body, cascade, confirmLabel,
  cancelLabel = "Keep it", icon, tone = "danger", isLoading,
}: OppConfirmProps) {
  const t = useOppTheme();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = React.useState(false);
  const c = tone === "danger" ? t.danger : t.warning;

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered motionPreset="slideInBottom">
      <AlertDialogOverlay>
        <AlertDialogContent bg={t.surface} borderRadius={t.radius} borderWidth="1px" borderColor={t.border} mx={4}>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={t.text}>
            <HStack spacing={3} align="center">
              <Flex flexShrink={0} w={10} h={10} borderRadius="lg" bg={alpha(c, 0.14)} align="center" justify="center">
                <Icon as={icon ?? (tone === "danger" ? FiTrash2 : FiAlertTriangle)} color={c} />
              </Flex>
              <Text>{title}</Text>
            </HStack>
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="md" color={t.muted}>{body}</Text>
              {cascade && cascade.length > 0 && (
                <Box borderRadius="xl" borderWidth="1px" borderColor={alpha(c, 0.3)} bg={alpha(c, 0.06)} p={3}>
                  <Text fontSize="md" fontWeight="semibold" color={t.text} mb={1}>This goes too:</Text>
                  <VStack as="ul" align="stretch" spacing={1} pl={4}>
                    {cascade.map((line) => (
                      <Text as="li" key={line} fontSize="md" color={t.muted}>{line}</Text>
                    ))}
                  </VStack>
                </Box>
              )}
              <Text fontSize="sm" color={t.faint}>This cannot be undone.</Text>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Stack direction={{ base: "column-reverse", sm: "row" }} spacing={3} w={{ base: "100%", sm: "auto" }}>
              <Button ref={cancelRef} onClick={blurClick(onClose)} variant="outline" size="lg" minH="48px" w={{ base: "100%", sm: "auto" }}>
                {cancelLabel}
              </Button>
              <Button
                colorScheme={tone === "danger" ? "red" : "orange"}
                size="lg"
                minH="48px"
                w={{ base: "100%", sm: "auto" }}
                isLoading={busy || isLoading}
                loadingText="Working…"
                onClick={() => { void run(); }}
              >
                {confirmLabel}
              </Button>
            </Stack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

/**
 * The state a page needs to drive one shared `<OppConfirm>` for a list of rows, so a page does not grow
 * one piece of dialog state per button. `ask({...})` opens it; the dialog closes itself when the work settles.
 */
export function useOppConfirm() {
  const [pending, setPending] = React.useState<Omit<OppConfirmProps, "isOpen" | "onClose"> | null>(null);
  const close = React.useCallback(() => setPending(null), []);
  const ask = React.useCallback((p: Omit<OppConfirmProps, "isOpen" | "onClose">) => setPending(p), []);
  const dialog = pending ? (
    <OppConfirm
      {...pending}
      isOpen
      onClose={close}
      onConfirm={async () => {
        await pending.onConfirm();
        close();
      }}
    />
  ) : null;
  return { ask, dialog, close };
}
